/**
 * GuestExportModal.jsx — Section 10.1
 *
 * Guest export: email-only modal.
 * Exact copy: "We will email your beat to you."
 * Triggers MP3 render via same export engine as logged-in users.
 */

import React, { useEffect, useState } from 'react';
import { Download, Mail, X, Loader2 } from 'lucide-react';
import QRCode from 'qrcode';
import { buildGuestListenUrl } from '@/utils/expoExport';

export const GuestExportModal = ({ isOpen, onClose, onExport, isExporting, kiosk = false, onNextStudent }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);

  useEffect(() => {
    const url = buildGuestListenUrl(result?.listen_url);
    if (!url) return;
    QRCode.toDataURL(url, { width: 220, margin: 1 }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [result]);
  useEffect(() => {
    if (!isOpen) { setSubmitted(false); setResult(null); setQrDataUrl(null); setError(null); setEmail(''); }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setError(null);
    try {
      const exportResult = await onExport?.(email);
      setResult(exportResult);
      setSubmitted(true);
    } catch (e) {
      setError(e.message || 'Export failed');
    }
  };

  if (submitted) {
    return (
      <div data-testid="guest-export-modal" className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
        <div className="flex flex-col items-center" style={{ width: 420, background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)', padding: '40px 32px' }}>
          <img src="/pizzul/satisfied.png" alt="Pizzul is satisfied with your beat" style={{ width: 96, height: 96, marginBottom: 8, objectFit: 'contain' }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Your beat is ready!</span>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.6, marginBottom: 24 }}>
            {result?.email_status === 'sent'
              ? `Check your email at ${email} for your MP3.`
              : result?.email_status === 'failed'
                ? "Email didn't go through. Scan the code to keep your beat."
                : "Your email may take a minute. Scan the code now so you don't lose your beat."}
          </p>
          {qrDataUrl && <>
            <img src={qrDataUrl} alt="QR code to hear your beat" style={{ width: 180, height: 180, background: '#fff', padding: 8, borderRadius: 8, marginBottom: 8 }} />
            <p style={{ fontSize: 12, color: 'var(--text-dim)', textAlign: 'center', marginBottom: 16 }}>Scan to hear your beat on a phone.</p>
          </>}
          <button
            onClick={async () => {
              if (kiosk) await onNextStudent?.();
              onClose();
            }}
            style={{
              height: 40, borderRadius: 22, padding: '0 28px',
              fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: 'var(--green)', color: 'var(--bg)',
            }}
          >
            {kiosk ? 'Next student' : 'Done'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="guest-export-modal" className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="flex flex-col" style={{ width: 420, background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <Download size={18} style={{ color: 'var(--green)' }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Export Your Beat</span>
          </div>
          <button data-testid="close-guest-export" onClick={onClose} className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--bg-elevated)', color: 'var(--text-dim)' }}>
            <X size={14} />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col items-center py-6 px-6 gap-4">
          <img src="/Pizzul_Hero.png" alt="Pizzul" style={{ width: 56, height: 56, borderRadius: '50%', border: '2px solid var(--green)', objectFit: 'cover' }} />

          <p data-testid="guest-export-copy" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', textAlign: 'center' }}>
            Where should Pizzul send your beat? Ask a grown-up for their email.
          </p>

          <div className="w-full flex flex-col gap-2">
            <div className="flex items-center gap-2 w-full" style={{ background: 'var(--bg-elevated)', borderRadius: 22, border: '1px solid var(--border)', padding: '0 16px', height: 44 }}>
              <Mail size={16} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
              <input
                data-testid="guest-email-input"
                type="email"
                placeholder="Grown-up's email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                className="flex-1 bg-transparent border-none outline-none"
                style={{ fontSize: 14, color: 'var(--text)' }}
              />
            </div>
            {error && (
              <span data-testid="guest-export-error" style={{ fontSize: 12, color: 'var(--pink)', paddingLeft: 16 }}>{error}</span>
            )}
          </div>
          <p className="w-full" style={{ fontSize: 12, color: 'var(--text-dim)', margin: '-6px 0 0' }}>We'll email the beat to this address. We never sell emails.</p>

          <button
            data-testid="guest-export-submit"
            onClick={handleSubmit}
            disabled={isExporting}
            className="flex items-center justify-center gap-2 w-full"
            style={{
              height: 44, borderRadius: 22,
              fontSize: 14, fontWeight: 600,
              background: isExporting ? 'var(--bg-elevated)' : 'var(--green)',
              color: isExporting ? 'var(--text-dim)' : 'var(--bg)',
              border: 'none', cursor: isExporting ? 'not-allowed' : 'pointer',
            }}
          >
            {isExporting ? <><Loader2 size={16} className="animate-spin" /> Rendering...</> : <><Download size={16} /> Send My Beat</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestExportModal;
